import React, { useState, useEffect, useId, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../ui/UIElements/Button";
import { Icon } from "../../ui/UIElements/Icon";
import { Label } from "../../ui/UIElements/Label";
import { NativeSelect } from "../../ui/UIElements/Select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../ui/UIElements/Popover";
import { useUiLocale } from "../../../hooks/useUiLocale";
import { formatDateByLocale } from "../../../lib/localeFormatters";
import {
  clampDateToMonthFilterRange,
  getMonthFilterMaxDate,
  getMonthFilterYears,
  getSelectableMonthIndices,
} from "../../../lib/reportUtils";

/**
 * MonthYearPicker Component
 * Simple picker that shows only month and year dropdowns
 */
const MonthYearPicker = ({
  value,
  onChange,
  placeholder,
  label,
  subtitle,
  id,
  className,
}) => {
  const { t } = useTranslation("reports");
  const { locale } = useUiLocale();
  const generatedId = useId();
  const pickerId = id || `month-year-picker-${generatedId.replace(/:/g, "")}`;
  const monthSelectId = `${pickerId}-month`;
  const yearSelectId = `${pickerId}-year`;
  const maxDate = getMonthFilterMaxDate();
  const clampedValue = value ? clampDateToMonthFilterRange(value) : null;
  const fallbackYear = clampedValue?.getFullYear() ?? maxDate.getFullYear();
  const fallbackMonth = clampedValue?.getMonth() ?? maxDate.getMonth();
  const [open, setOpen] = useState(false);
  const [tempMonth, setTempMonth] = useState(fallbackMonth);
  const [tempYear, setTempYear] = useState(fallbackYear);

  const months = Array.from({ length: 12 }, (_, index) =>
    new Intl.DateTimeFormat(locale, { month: "long" }).format(
      new Date(2020, index, 1),
    ),
  );

  const years = getMonthFilterYears();
  const availableMonths = useMemo(
    () => getSelectableMonthIndices(tempYear),
    [tempYear],
  );

  const handleConfirm = () => {
    const newDate = clampDateToMonthFilterRange(
      new Date(tempYear, tempMonth, 1),
    );
    onChange?.(newDate);
    setOpen(false);
  };

  const handleCancel = () => {
    setTempMonth(fallbackMonth);
    setTempYear(fallbackYear);
    setOpen(false);
  };

  // Sync temp values with value when popover opens
  useEffect(() => {
    if (open) {
      setTempMonth(fallbackMonth);
      setTempYear(fallbackYear);
    }
  }, [open, fallbackMonth, fallbackYear]);

  // Keep month valid when switching years with different month bounds.
  useEffect(() => {
    if (!availableMonths.length) return;
    if (!availableMonths.includes(tempMonth)) {
      setTempMonth(availableMonths[availableMonths.length - 1]);
    }
  }, [availableMonths, tempMonth]);

  const formatDisplayValue = (date) => {
    if (!date) {
      return placeholder || t("monthYearPicker.placeholder");
    }

    return formatDateByLocale(
      date,
      {
        year: "numeric",
        month: "long",
      },
      locale,
    );
  };

  return (
    <div className={`ordering-month-picker ${className || ""}`}>
      <div className="flex flex-col gap-3">
        {label && (
          <Label htmlFor={pickerId} className="text-foreground px-1">
            {label}
          </Label>
        )}
        {subtitle && (
          <span className="text-muted-foreground text-xs">{subtitle}</span>
        )}
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outlineLight"
              id={pickerId}
              className="ordering-month-picker__trigger border-border bg-card text-foreground hover:bg-accent focus:ring-ring h-9 w-full justify-between font-normal focus:ring-2"
            >
              {formatDisplayValue(clampedValue)}
              <Icon name="ChevronDown" className="h-4 w-4 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="ordering-month-picker__popover border-border bg-card w-auto p-4"
            align="start"
          >
            <div className="space-y-4">
              <div className="flex gap-3">
                <div className="flex-1">
                  <label
                    htmlFor={monthSelectId}
                    className="text-muted-foreground mb-1 block text-xs font-medium"
                  >
                    {t("monthYearPicker.month")}
                  </label>
                  <NativeSelect
                    id={monthSelectId}
                    value={tempMonth}
                    onChange={(e) => setTempMonth(parseInt(e.target.value))}
                    className="ordering-month-picker__select custom-select border-border bg-muted text-foreground focus:ring-ring w-full min-w-[140px] rounded-md border px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:outline-none"
                  >
                    {availableMonths.map((monthIndex) => (
                      <option
                        key={monthIndex}
                        value={monthIndex}
                        className="bg-muted text-foreground"
                      >
                        {months[monthIndex]}
                      </option>
                    ))}
                  </NativeSelect>
                </div>
                <div className="flex-1">
                  <label
                    htmlFor={yearSelectId}
                    className="text-muted-foreground mb-1 block text-xs font-medium"
                  >
                    {t("monthYearPicker.year")}
                  </label>
                  <NativeSelect
                    id={yearSelectId}
                    value={tempYear}
                    onChange={(e) => setTempYear(parseInt(e.target.value))}
                    className="ordering-month-picker__select custom-select border-border bg-muted text-foreground focus:ring-ring w-full rounded-md border px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:outline-none"
                  >
                    {years.map((year) => (
                      <option
                        key={year}
                        value={year}
                        className="bg-muted text-foreground"
                      >
                        {year}
                      </option>
                    ))}
                  </NativeSelect>
                </div>
              </div>
              <div className="border-border flex justify-end gap-2 border-t pt-2">
                <Button
                  variant="outlineLight"
                  size="sm"
                  onClick={handleCancel}
                  className="border-border bg-muted text-foreground hover:bg-accent"
                >
                  {t("monthYearPicker.cancel")}
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleConfirm}
                  className="bg-primary text-primary-foreground hover:bg-primary"
                >
                  {t("monthYearPicker.select")}
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};

export default MonthYearPicker;
